import { ShieldCheck, Star, Loader2, Inbox, AlertTriangle } from "lucide-react";

export function VerifiedBadge({ className = "" }) {
  return (
    <span
      data-testid="verified-badge"
      className={`inline-flex items-center gap-1.5 rounded-full bg-[#E7F0EE] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-teal-600 ${className}`}
    >
      <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Verified employee
    </span>
  );
}

const STATUS_MAP = {
  under_review: { label: "Under review", cls: "border-info-500/40 bg-info-500/10 text-info-500" },
  published: { label: "Published", cls: "border-success-500/40 bg-success-500/10 text-success-500" },
  declined: { label: "Declined", cls: "border-coral-500/40 bg-coral-500/10 text-coral-500" },
};

export function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.under_review;
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center rounded-full border px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

export function StarRating({ value = 0, size = 16 }) {
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          className={i <= full ? "fill-amber-500 text-amber-500" : "text-slate-600"}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

export function ScoreRing({ value }) {
  const pct = (value / 5) * 100;
  const color = value >= 4 ? "#12766B" : value >= 2.5 ? "#D9930D" : "#C4362F";
  return (
    <div className="relative h-20 w-20" aria-label={`Score ${value} of 5`}>
      <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#DDDAD1" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${pct} 100`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading text-xl font-bold text-ink">
        {value ? value.toFixed(1) : "-"}
      </span>
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div data-testid="loading-state" className="flex flex-col items-center justify-center gap-3 py-24 text-slate-400">
      <Loader2 className="h-7 w-7 animate-spin text-teal-400" aria-hidden="true" />
      <p className="text-sm">{label}…</p>
    </div>
  );
}

export function EmptyState({ title, subtitle, icon: Icon = Inbox, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center gap-3 rounded-[10px] border border-dashed border-[#DDDAD1] py-20 text-center">
      <Icon className="h-9 w-9 text-slate-500" aria-hidden="true" />
      <h3 className="text-lg font-semibold text-ink">{title}</h3>
      {subtitle && <p className="max-w-md text-sm text-slate-400">{subtitle}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message }) {
  return (
    <div data-testid="error-state" className="flex flex-col items-center justify-center gap-2 py-20 text-center text-coral-500">
      <AlertTriangle className="h-8 w-8" aria-hidden="true" />
      <p className="text-sm">{message || "Something went wrong."}</p>
    </div>
  );
}

// Builds the reviewer metadata line based on the disclosure level they chose.
export function disclosureLine(r) {
  const status = r.employment_status === "Current" ? "Current employee" : "Former employee";
  const yrs = r.year_from && r.year_to ? `${r.year_from} to ${r.year_to}`
    : (r.year_from ? `since ${r.year_from}` : "");
  const level = r.disclosure_level || "moderate";
  if (level === "minimal") return [status, yrs].filter(Boolean).join(" · ");
  if (level === "detailed") return [r.job_title || status, r.market, yrs].filter(Boolean).join(" · ");
  return [r.department || status, r.market, yrs].filter(Boolean).join(" · ");
}
